// Hook orchestrating the HR Stats drill-down page state.
// State lives in the URL so views are shareable / refreshable.
//
// Filter values are arrays — the chip dropdowns are multi-select. URL
// encoding follows the standard "repeat the same key" convention so a
// query string like `?pillar=Enable&pillar=Sell&country=India` is
// interpreted as { pillar: ['Enable', 'Sell'], country: ['India'] }.

import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import { hrStatsApi } from '../services/api';
import {
  DEFAULT_HR_DIMENSION,
  HR_DIMENSIONS,
  nextDrillDimension,
} from '../constants/hrDimensions';
import type {
  HrDimension,
  HrEmployeeStatus,
  HrFilterMap,
  HrStatsBreakdownResponse,
  HrStatsSummaryResponse,
} from '../types/hrStats';

const ALLOWED_DIMENSION_IDS: ReadonlySet<string> = new Set(HR_DIMENSIONS.map((d) => d.id));
const ALLOWED_STATUS: ReadonlySet<string> = new Set<HrEmployeeStatus>([
  'ACTIVE',
  'INACTIVE',
  'BOTH',
]);

const GROUP_BY_PARAM = 'groupBy';
const STATUS_PARAM = 'status';
/** Reserved query-string keys that are NOT drill filters. */
const RESERVED_PARAMS = new Set<string>([GROUP_BY_PARAM, STATUS_PARAM]);

export interface UseHrStatsResult {
  // State
  groupBy: HrDimension;
  filters: HrFilterMap;
  employeeStatus: HrEmployeeStatus;

  // Data
  breakdown: HrStatsBreakdownResponse | undefined;
  isBreakdownLoading: boolean;
  isBreakdownError: boolean;

  summary: HrStatsSummaryResponse | undefined;
  isSummaryLoading: boolean;

  // Actions
  setGroupBy: (dim: HrDimension) => void;
  setEmployeeStatus: (status: HrEmployeeStatus) => void;
  /** Replace the selection for a single dimension (multi-select aware).
   *  An empty array clears the filter. */
  setFilter: (dim: HrDimension, values: string[]) => void;
  /** Apply a single value AND advance group_by to the next un-used
   *  dimension — used by drill-row clicks (e.g. clicking a country in
   *  the geo section). Single-pick replacement, not additive. */
  drillInto: (dim: HrDimension, value: string) => void;
  removeFilter: (dim: HrDimension) => void;
  clearAllFilters: () => void;
}

function isHrDimension(value: string | null): value is HrDimension {
  return value != null && ALLOWED_DIMENSION_IDS.has(value);
}

function isEmployeeStatus(value: string | null): value is HrEmployeeStatus {
  return value != null && ALLOWED_STATUS.has(value);
}

export function useHrStats(): UseHrStatsResult {
  const [searchParams, setSearchParams] = useSearchParams();

  const groupBy: HrDimension = useMemo(() => {
    const raw = searchParams.get(GROUP_BY_PARAM);
    return isHrDimension(raw) ? raw : DEFAULT_HR_DIMENSION;
  }, [searchParams]);

  const employeeStatus: HrEmployeeStatus = useMemo(() => {
    const raw = searchParams.get(STATUS_PARAM);
    return isEmployeeStatus(raw) ? raw : 'ACTIVE';
  }, [searchParams]);

  const filters: HrFilterMap = useMemo(() => {
    const out: HrFilterMap = {};
    // Walk the unique keys, then collect getAll() so multi-value params
    // become arrays. searchParams.forEach iterates each occurrence which
    // would clobber repeated keys.
    const seen = new Set<string>();
    searchParams.forEach((_, key) => {
      if (seen.has(key)) return;
      seen.add(key);
      if (RESERVED_PARAMS.has(key)) return;
      if (!isHrDimension(key)) return;
      const values = searchParams
        .getAll(key)
        .map((v) => v.trim())
        .filter(Boolean);
      if (values.length > 0) out[key] = Array.from(new Set(values));
    });
    return out;
  }, [searchParams]);

  // ---------------------------------------------------------------- queries

  const breakdownQuery = useQuery({
    queryKey: ['hr-stats', 'breakdown', groupBy, filters, employeeStatus],
    queryFn: () =>
      hrStatsApi.getBreakdown({
        group_by: groupBy,
        filters,
        employee_status: employeeStatus,
      }),
    staleTime: 60 * 1000,
  });

  const summaryQuery = useQuery({
    queryKey: ['hr-stats', 'summary', filters, employeeStatus],
    queryFn: () =>
      hrStatsApi.getSummary({ filters, employee_status: employeeStatus }),
    staleTime: 60 * 1000,
  });

  // ---------------------------------------------------------------- actions

  const updateParams = useCallback(
    (mutator: (params: URLSearchParams) => void) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          mutator(next);
          return next;
        },
        { replace: false },
      );
    },
    [setSearchParams],
  );

  const setGroupBy = useCallback(
    (dim: HrDimension) => {
      updateParams((p) => {
        p.set(GROUP_BY_PARAM, dim);
      });
    },
    [updateParams],
  );

  const setEmployeeStatus = useCallback(
    (status: HrEmployeeStatus) => {
      updateParams((p) => {
        p.set(STATUS_PARAM, status);
      });
    },
    [updateParams],
  );

  const setFilter = useCallback(
    (dim: HrDimension, values: string[]) => {
      const cleaned = Array.from(
        new Set(values.map((v) => v.trim()).filter(Boolean)),
      );
      updateParams((p) => {
        p.delete(dim);
        cleaned.forEach((v) => p.append(dim, v));
      });
    },
    [updateParams],
  );

  const drillInto = useCallback(
    (dim: HrDimension, value: string) => {
      const trimmed = value.trim();
      if (!trimmed) return;
      const advance = nextDrillDimension(dim, {
        ...filters,
        [dim]: [trimmed],
      });
      updateParams((p) => {
        p.delete(dim);
        p.append(dim, trimmed);
        if (advance) p.set(GROUP_BY_PARAM, advance);
      });
    },
    [filters, updateParams],
  );

  const removeFilter = useCallback(
    (dim: HrDimension) => {
      updateParams((p) => {
        p.delete(dim);
      });
    },
    [updateParams],
  );

  const clearAllFilters = useCallback(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams();
        const status = prev.get(STATUS_PARAM);
        const group = prev.get(GROUP_BY_PARAM);
        if (status) next.set(STATUS_PARAM, status);
        if (group) next.set(GROUP_BY_PARAM, group);
        return next;
      },
      { replace: false },
    );
  }, [setSearchParams]);

  return {
    groupBy,
    filters,
    employeeStatus,
    breakdown: breakdownQuery.data,
    isBreakdownLoading: breakdownQuery.isLoading,
    isBreakdownError: breakdownQuery.isError,
    summary: summaryQuery.data,
    isSummaryLoading: summaryQuery.isLoading,
    setGroupBy,
    setEmployeeStatus,
    setFilter,
    drillInto,
    removeFilter,
    clearAllFilters,
  };
}
